/**
 * Fias Leads — primary navigation tab bar.
 *
 * Mirror of Fias360Nav. Tab labels stay generic (not vocabulary-
 * mapped) since Leads pre-sale terminology is fairly standard
 * across industries. Active tab is detected from the URL via
 * useLocation; detail routes count as active under their parent
 * tab (a deal detail page still highlights "Deals").
 */

import { Link, useLocation } from 'react-router-dom';
import {
  Building2,
  Users,
  TrendingUp,
  CheckSquare,
  Activity,
  Mail,
  PieChart,
  BarChart3,
  Upload,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { ARCHE_PATH } from '../constants';

interface Tab {
  label: string;
  path: string;
  icon: LucideIcon;
  isActive: (pathname: string) => boolean;
}

export function FiasLeadsNav() {
  const { pathname } = useLocation();

  const tabs: ReadonlyArray<Tab> = [
    {
      label: 'Dashboard',
      path: ARCHE_PATH,
      icon: BarChart3,
      isActive: (p) => p === ARCHE_PATH || p === `${ARCHE_PATH}/`,
    },
    {
      label: 'Companies',
      path: `${ARCHE_PATH}/companies`,
      icon: Building2,
      isActive: (p) => p.startsWith(`${ARCHE_PATH}/companies`),
    },
    {
      label: 'Contacts',
      path: `${ARCHE_PATH}/contacts`,
      icon: Users,
      isActive: (p) => p.startsWith(`${ARCHE_PATH}/contacts`),
    },
    {
      label: 'Deals',
      path: `${ARCHE_PATH}/deals`,
      icon: TrendingUp,
      isActive: (p) => p.startsWith(`${ARCHE_PATH}/deals`),
    },
    {
      label: 'Tasks',
      path: `${ARCHE_PATH}/tasks`,
      icon: CheckSquare,
      isActive: (p) => p.startsWith(`${ARCHE_PATH}/tasks`),
    },
    {
      label: 'Activities',
      path: `${ARCHE_PATH}/activities`,
      icon: Activity,
      isActive: (p) => p.startsWith(`${ARCHE_PATH}/activities`),
    },
    {
      label: 'Templates',
      path: `${ARCHE_PATH}/templates`,
      icon: Mail,
      isActive: (p) => p.startsWith(`${ARCHE_PATH}/templates`),
    },
    {
      label: 'Reports',
      path: `${ARCHE_PATH}/reports`,
      icon: PieChart,
      isActive: (p) => p.startsWith(`${ARCHE_PATH}/reports`),
    },
  ];

  const utilityTabs: ReadonlyArray<Tab> = [
    {
      label: 'Import',
      path: `${ARCHE_PATH}/import`,
      icon: Upload,
      isActive: (p) => p.startsWith(`${ARCHE_PATH}/import`),
    },
    {
      label: 'Settings',
      path: `${ARCHE_PATH}/settings`,
      icon: Settings,
      isActive: (p) => p.startsWith(`${ARCHE_PATH}/settings`),
    },
  ];

  return (
    <nav className="border-b border-border bg-card" aria-label="Fias Leads primary navigation">
      <div className="flex items-center px-6 overflow-x-auto scrollbar-hide">
        <ul className="flex items-center gap-1 flex-1">
          {tabs.map((tab) => (
            <TabItem key={tab.path} tab={tab} active={tab.isActive(pathname)} />
          ))}
        </ul>
        <ul className="flex items-center gap-1 pl-4 border-l border-border ml-4">
          {utilityTabs.map((tab) => (
            <TabItem key={tab.path} tab={tab} active={tab.isActive(pathname)} />
          ))}
        </ul>
      </div>
    </nav>
  );
}

function TabItem({ tab, active }: { tab: Tab; active: boolean }) {
  const Icon = tab.icon;
  return (
    <li className="relative">
      <Link
        to={tab.path}
        className={
          'inline-flex items-center gap-2 px-3 py-3 text-sm transition-colors ' +
          (active ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground')
        }
        aria-current={active ? 'page' : undefined}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span>{tab.label}</span>
      </Link>
      {active && (
        <span
          aria-hidden="true"
          className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-primary"
        />
      )}
    </li>
  );
}
